import { quotaRemaining } from "@/lib/poQuota";
import {
  formatPrice,
  getProductPrice,
  resolveSelectionPrice,
  sellableSelectionPrice,
} from "@/lib/pricing";
import { isStockTracked, type Product } from "@/lib/products";
import type { ProductPromo } from "@/lib/promo";
import {
  buildPriceRangeLabel,
  buildProductShareText,
  buildStockLines,
  type StockShareVariant,
} from "@/lib/shareProduct";
import type { WaTemplateOverrides } from "@/lib/waTemplates";

/**
 * Isi pesan "Bagikan ke WA" sebuah produk — SATU sumber untuk halaman produk
 * (`ProductDetail`) dan feed Jelajah, supaya pesan yang dikirim dari kedua
 * tempat itu identik: rentang harga publik, jenis, blok stok / sisa kuota per
 * varian, dan tautannya.
 *
 * Dipindah APA ADANYA dari `ProductDetail` (2026-09-24); aturan & alasannya
 * tetap di komentar masing-masing fungsi. Semuanya murni dan SINKRON — handler
 * tombol Bagikan wajib sinkron (lihat `shareOpensWhatsAppDirectly.test.ts`),
 * jadi angka stoknya harus sudah dibaca di latar oleh pemanggil.
 */

/** Potret stok yang dibaca di latar (`fetchProductStockSnapshot`), bentuk strukturalnya. */
export type ShareStockSnapshot = {
  stock: Record<string, number>;
  quota: Record<string, number | null>;
  quotaUsed: Record<string, number>;
};

export type ShareStockInput = {
  /** Potret latar terakhir yang BERHASIL dibaca, atau null. */
  stockRead: { snapshot: ShareStockSnapshot; readAt: Date } | null;
  /** Peta kuota terpakai yang dipakai layar (fail-open) — cadangan bila potret tak ada. */
  quotaUsed: Record<string, number>;
  /** Cap waktu cadangan: kapan data produk di tangan pemanggil diterima. */
  fallbackReadAt: Date;
};

/**
 * Produk ini punya angka yang layak dicetak di pesan bagikan? PO biasa tak
 * punya (barangnya diproduksi mengikuti pesanan), dan mode malas sengaja tak
 * mengirim variannya ke browser.
 */
export function productShareNeedsStock(product: Product): boolean {
  return (
    (isStockTracked(product) || (product.poLimited ?? false)) &&
    !(product.variantsLazy ?? false) &&
    product.variations.length > 0
  );
}

/** Label jenis di pesan bagikan. */
export function shareTypeLabel(product: Product): string {
  return isStockTracked(product) ? "Ready Stock" : "Pre-Order";
}

/**
 * Label harga pesan bagikan. Varian yang harganya BEDA-BEDA dirangkum jadi
 * RENTANG ("Rp 145.000-155.000") — sebelumnya satu angka perwakilan, yang di
 * produk berharga campuran memajang harga varian termurah untuk seluruh
 * kombinasi. Angkanya lewat mesin harga yang SAMA dengan layar
 * (`resolveSelectionPrice`), jadi mustahil menyimpang dari yang dilihat
 * pembeli. Grup sengaja `null` = HARGA PUBLIK supaya harga khusus reseller
 * tak bocor, TAPI promo kampanye ikut (mig 3470): harga publik saat promo
 * memang harga promo, dan itu justru materi promosinya.
 *
 * Varian tanpa harga sendiri di produk berharga campuran dipulangkan
 * `undefined` oleh `sellableSelectionPrice` dan TIDAK ikut membentuk rentang
 * — angka perwakilan varian lain tak boleh jadi klaim harga (natasyashop
 * #2095).
 */
export function sharePriceLabel(
  product: Product,
  promos: readonly ProductPromo[]
): string | null {
  const prices: number[] = [];
  for (const variation of product.variations) {
    const price = sellableSelectionPrice(
      resolveSelectionPrice(
        variation,
        {
          variations: product.variations,
          priceNormal: product.price_normal,
          priceRange: product.variantPriceRange,
          priceUncertain: product.variantPriceUncertain,
          // Sumbu harga (mig 7680): sama dengan halaman produk — fail-closed.
          priceAxis: product.priceAxis,
        },
        null,
        promos
      )
    ).price;
    if (price !== undefined) prices.push(price);
  }
  if (prices.length > 0) {
    return buildPriceRangeLabel(Math.min(...prices), Math.max(...prices));
  }
  // Mode malas (varian tak dikirim ke browser) & produk tanpa varian berharga:
  // perilaku lama — satu angka perwakilan.
  const single = getProductPrice(product, null, promos);
  return single !== undefined ? formatPrice(single) : null;
}

/**
 * Blok stok / sisa kuota untuk pesan WhatsApp.
 *
 * Kalau potret latarnya belum/gagal terbaca:
 *  - ready stock → pakai angka yang sudah di tangan pemanggil, dicap
 *    `fallbackReadAt` (angka itu datang dari data yang benar-benar dibaca,
 *    bukan tebakan);
 *  - PO berkuota → blok DIBUANG seluruhnya. Peta "terpakai" di layar sengaja
 *    fail-open (lihat `fetchPreorderQuotaUsed`) sehingga kegagalannya tak
 *    bisa dibedakan dari "belum ada yang memesan" — dan sisa kuota yang
 *    dilaporkan terlalu besar adalah janji yang akan ditolak trigger
 *    `enforce_preorder_quota` saat pembeli menekan Tambah ke Keranjang.
 */
export function shareStockLines(
  product: Product,
  input: ShareStockInput
): string[] | undefined {
  if (!productShareNeedsStock(product)) return undefined;
  const stockTracked = isStockTracked(product);
  const poLimited = product.poLimited ?? false;

  const snapshot = input.stockRead?.snapshot ?? null;
  if (!snapshot && poLimited) return undefined;
  const readAt = input.stockRead?.readAt ?? input.fallbackReadAt;

  const variants: StockShareVariant[] = [];
  for (const variation of product.variations) {
    if (stockTracked) {
      const stock = snapshot ? snapshot.stock[variation.id] : variation.stockQty;
      if (stock === undefined) continue;
      variants.push({
        name: variation.name,
        optionIds: variation.optionIds,
        qty: Math.max(0, Math.floor(stock)),
      });
      continue;
    }
    const quota = snapshot ? snapshot.quota[variation.id] : variation.poQuota;
    // Varian tanpa angka jatah = TAK TERBATAS, bukan nol. Mencetaknya "0 Pcs"
    // menyatakan habis; mencetak angka apa pun mengarang batas yang tak ada.
    // Jadi ia dilewati — halaman produknya tetap menerima pesanan.
    if (quota == null) continue;
    const sisa = quotaRemaining(
      true,
      quota,
      (snapshot ? snapshot.quotaUsed : input.quotaUsed)[variation.id]
    );
    if (sisa === null) continue;
    variants.push({
      name: variation.name,
      optionIds: variation.optionIds,
      qty: sisa,
    });
  }
  if (variants.length === 0) return undefined;

  return buildStockLines({
    kind: stockTracked ? "stock" : "quota",
    groups: product.variantGroups.map((group) => ({
      options: group.options.map((option) => ({
        id: option.id,
        name: option.name,
      })),
    })),
    variants,
    readAt,
  });
}

/**
 * Pesan bagikan utuh (templat WA `bagikan_produk`). `stock: null` = tanpa blok
 * stok — dipakai bila data varian lengkapnya tak tersedia, supaya pesan tak
 * pernah mencetak rincian dari data yang tak lengkap (mis. kartu feed yang
 * hanya membawa SATU varian perwakilan).
 */
export function productShareMessage(
  product: Product,
  opts: {
    url: string;
    storeLabel: string | null;
    promos: readonly ProductPromo[];
    stock: ShareStockInput | null;
  },
  waTemplates?: WaTemplateOverrides | null
): string {
  return buildProductShareText(
    {
      productName: product.name,
      storeName: opts.storeLabel,
      priceLabel: sharePriceLabel(product, opts.promos),
      typeLabel: shareTypeLabel(product),
      stockLines: opts.stock ? shareStockLines(product, opts.stock) : undefined,
      url: opts.url,
    },
    waTemplates
  );
}
